import { PageTitle } from "@/components/custom/PageTitle"
import { workflowRunHistoryColumns } from './Table/WorkflowRun/WorkflowHistoryColumns'
import { WorkflowsExecutionHistoryTable } from "./Table/WorkflowRun/WorkflowRunHistoryDataTable"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { getWorkflowExecutions } from "@/services/WorkflowExecutionService"
import { WorkflowExecution } from "@/types/workflowHistory"
import { BackButton } from "@/components/custom/BackButton"
import { workflowRunHistoryActions } from "@/redux/slice/workflowRunHistory"
import { WorkflowRunStatusType } from "@/constants/workflowHistory/workflowHistory"

export const WorkflowHistory = () => {
  const { workflowId } = useParams()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const { workflows } = useSelector((state: RootState) => state.workflow)
  const [workflowName, setWorkflowName] = useState('')
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowExecution[]>([])
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  const fetchWorkflowExecutions = async (workflowId: string, workflowName: string, shouldLoad = true) => {
    if (!token) return
    const resp = await dispatch(getWorkflowExecutions(token, workflowId, shouldLoad))
    if (resp.data?.workflowExecutions) {
      setWorkflowRuns(resp.data.workflowExecutions)

      dispatch(workflowRunHistoryActions.setWorkflowRun({
        workflowId,
        workflowName,
        workflowRuns: resp.data.workflowExecutions
      }))

      const hasActiveRuns = resp.data.workflowExecutions.some(
        (run: WorkflowExecution) =>
          run.status?.toLowerCase() === WorkflowRunStatusType.PROCESSING ||
          run.status?.toLowerCase() === WorkflowRunStatusType.READY
      )

      if (hasActiveRuns && !pollingRef.current) {
        pollingRef.current = setInterval(() => {
          fetchWorkflowExecutions(workflowId, workflowName, false) // no loading for polling
        }, 30000) // 30 seconds
      } else if (!hasActiveRuns && pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    } else if (resp.err) {
      console.error(resp.err)
    }
  }

  useEffect(() => {
    if (!token) return

    const validWorkflow = workflows.find(w => w.id === workflowId)
    if (!validWorkflow) {
      return navigate('/workflow-history')
    }

    setWorkflowName(validWorkflow.nickname)
    fetchWorkflowExecutions(validWorkflow.id, validWorkflow.nickname)

    // Cleanup polling when unmounting
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, token, workflowId, workflows])

  return (
    <div className="protectedPageLayout">
      <BackButton buttonText="Back to Workflow History" buttonLink="/workflow-history" />
      <div className="flex items-center justify-between">
        <PageTitle title={workflowName} />
      </div>
      <WorkflowsExecutionHistoryTable
        columns={workflowRunHistoryColumns}
        data={workflowRuns}
      />
    </div>
  )
}
